/**
 * Modale « Nouvelle équipe » — 219:6074/219:6096. Champ Organisation en liste déroulante,
 * par défaut l'organisation du groupe d'où l'on a cliqué (`defaultOrganizationId`, § liste
 * groupée par organisation, `TeamsPage`), sinon l'organisation courante
 * (`currentOrganizationId`, § sélecteur d'équipe, `TeamDetailPage`).
 *
 * Le serveur accepte déjà `organizationId` en option (`teamCreateSchema`, S2(c)) : absent du
 * corps de la requête, il place l'équipe dans l'espace personnel. La liste déroulante porte
 * donc toujours « Mon espace » (valeur `''`) en plus de l'organisation (ORG-8) — `submit`
 * n'envoie `organizationId` que s'il est non vide.
 *
 * La liste reçue est filtrée ICI, pour tous les appelants (`TeamsPage`, `TeamSwitcher`) : seules
 * les organisations à adhésion `active` sont proposées (I2 — un compte `requested`/`suspended`
 * y serait refusé), et jamais l'espace personnel (I3 — il est déjà « Mon espace », ORG-8).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { appAlert } from './service.js';
import { Modal } from '../components/Modal.jsx';
import {
  BTN_GHOST_SM,
  BTN_PRIMARY_SM,
  FORM_FIELD,
  FORM_LABEL,
  INPUT,
  SELECT,
} from '../ui/theme.js';

export function CreateTeamDialog({
  organizations,
  defaultOrganizationId,
  currentOrganizationId,
  onClose,
  onCreated,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const choix = organizations.filter(
    (organization) => organization.kind !== 'personnel' && organization.status === 'active',
  );
  const [organizationId, setOrganizationId] = useState(() => {
    const voulu = defaultOrganizationId ?? currentOrganizationId ?? choix[0]?.id ?? '';
    // Une équipe personnelle (ou d'une organisation non active) comme contexte → « Mon espace ».
    return choix.some((organization) => organization.id === voulu) ? voulu : '';
  });
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      // `organizationId` vide → l'espace personnel : ne pas l'envoyer, le serveur y place
      // l'équipe faute d'`organizationId` dans le corps de la requête (ORG-8).
      const payload = organizationId
        ? { name: name.trim(), organizationId }
        : { name: name.trim() };
      const { data } = await api.post('/teams', payload);
      onCreated(data);
    } catch (error) {
      await appAlert(t('teams.createFailed', { message: error.message }), {
        title: t('teams.createPromptTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t('teams.createPromptTitle')} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className={FORM_FIELD}>
          <span className={FORM_LABEL}>{t('teams.createOrganizationLabel')}</span>
          <select
            className={SELECT}
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
            disabled={busy}
          >
            <option value="">{t('space.personal')}</option>
            {choix.map((organization) => (
              <option key={organization.id} value={organization.id}>
                {organization.name}
              </option>
            ))}
          </select>
        </label>
        <label className={FORM_FIELD}>
          <span className={FORM_LABEL}>{t('teams.createNameLabel')}</span>
          <input
            className={INPUT}
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            required
          />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className={BTN_GHOST_SM} onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button type="submit" className={BTN_PRIMARY_SM} disabled={busy}>
            {t('teams.createSubmit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
